import * as React from 'react'

import styled from 'styled-components'
import { useSpring, animated } from 'react-spring'
import Link from 'next/link'

import PreviewSocial from '../../preview/PreviewSocial'
import PreviewTime from '../../preview/PreviewTime'
import { VideoData, VideoFullData } from '../../../interfaces'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import SkeletonCard from './SkeletonCard'
import { useRouter } from 'next/router'
import log from '../../../events/log'
import { ClickVideoCardEvent } from '../../../events/social_events'
import { LeaderboardTime } from '../../leaderboard'

const ItemDiv = styled(animated.div)`
  display: flex;
  flex-direction: column;
  position: relative;

  // mobile
  margin: 0 0 25px 0;
  width: 100%;
  background: linear-gradient(rgb(55, 55, 55), rgb(20, 20, 20));
  border-color: #323232;
  border-width: 1px;
  border-style: solid;
  border-radius: 10px;
  cursor: pointer;

  @media (min-width: 62em) {
    border-radius: 15px;
  }
`

const VideoThumbnailContainer = styled(animated.div)`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 7.5px;

  @media (min-width: 62em) {
    border-radius: 15px;
  }
`

const VideoThumbnail = styled(animated.div)<{ poster: string; isLive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background-color: black;
  cursor: pointer;
  background-image: url(${(props) => props.poster});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  opacity: ${(props) => (props.isLive ? '25%' : '100%')};

  @media (min-width: 62em) {
    border-radius: 15px;
  }
`

const PlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const DetailsDiv = styled(animated.div)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  margin: 10px;
  margin-top: -40px;
  z-index: 1;
`

const HeaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const VideoTitle = styled.div`
  color: white;
  text-align: left;
  cursor: pointer;
  font-weight: 700;
  font-size: 20px;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  margin-top: 10px;
`

const Details = styled.div`
  margin: 10px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const LeftStatDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RightStatDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const StatTitle = styled.div`
  color: white;
  font-weight: 400;
  font-size: 14px;
`

const Subtitle = styled.div`
  color: white;
  font-weight: 700;
  font-size: 18px;
`

const RankDiv = styled.div`
  position: absolute;
  z-index: 5;
  top: 15px;
  right: 15px;
  background: white;
  border-radius: 50%;
  /* padding: 10px; */
  min-width: 80px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0;
`

const Rank = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: black;
  margin: 0;
  margin-top: -5px;
`

const Time = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: black;
  margin-top: -5px;
`

export const BaseVideoCard = ({
  video: item,
  creator,
  primaryInfo,
  secondaryInfo,
  scheduled_time,
  leaderboardInfo,
  rank,
  time,
}: Omit<VideoFullData, 'video'> & {
  video?: VideoData
  rank?: number
  time?: LeaderboardTime
}): JSX.Element => {
  const [vidHover, setVidHover] = React.useState(false)
  const [isLive, setIsLive] = React.useState(true)
  const scheduledTime = scheduled_time || new Date()
  const router = useRouter()

  const vidHoverState = useSpring({
    transform: vidHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const thumbnailHoverState = useSpring({
    transform: vidHover ? `scale3d(1.05, 1.05, 1)` : `scale3d(1, 1, 1)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  React.useEffect(() => {
    setIsLive(scheduledTime <= new Date())
  }, [])

  if (!item) {
    return <SkeletonCard />
  }

  const title = item.title

  // TODO: be more smart about this! w=800&h=450
  const poster = `https://glassimg.imgix.net/${item.thumbnail_uri}?ar=16:9&fit=crop&w=800&h=450&fm=webp`

  const detailLink = `/watch/${item.address}`

  return (
    <ItemDiv
      onMouseEnter={() => setVidHover(true)}
      onMouseLeave={() => setVidHover(false)}
      style={vidHoverState}
      onClick={() => {
        log(new ClickVideoCardEvent(router.pathname, item.address, item.created_by))

        router.push(detailLink)
      }}
    >
      {rank && time && (
        <RankDiv>
          <Rank>{'#' + rank}</Rank>
          <Time>{time}</Time>
        </RankDiv>
      )}

      <VideoThumbnailContainer>
        <VideoThumbnail style={thumbnailHoverState} poster={poster} isLive={!isLive} />
        <PlayDiv>
          {!isLive && (
            <PreviewTime scheduledTime={scheduledTime} setIsLive={setIsLive} isLive={isLive} />
          )}
        </PlayDiv>
      </VideoThumbnailContainer>
      <DetailsDiv>
        <PreviewSocial user={creator} address={item.created_by} />
        <HeaderDiv>
          <Link href={detailLink}>
            <VideoTitle>{title}</VideoTitle>
          </Link>
          <StatTitle>
            {isLive
              ? primaryInfo?.sold === primaryInfo?.supply
                ? `BUY FROM SECONDARY`
                : `PRIMARY SALE`
              : `UPCOMING SALE`}
          </StatTitle>
          {primaryInfo && secondaryInfo && (
            <Details>
              <LeftStatDiv>
                <StatTitle>
                  {primaryInfo.sold === primaryInfo.supply
                    ? secondaryInfo.floor_price === 0
                      ? 'AVAILABILITY'
                      : `FLOOR PRICE`
                    : `PRICE`}
                </StatTitle>
                <Subtitle>
                  {primaryInfo.sold === primaryInfo.supply
                    ? secondaryInfo.floor_price === 0
                      ? 'SOLD OUT'
                      : `${
                          Math.round((secondaryInfo.floor_price / LAMPORTS_PER_SOL) * 100) / 100
                        } SOL`
                    : primaryInfo.price === 0
                    ? `FREE`
                    : `${Math.round((100 * primaryInfo.price) / LAMPORTS_PER_SOL) / 100} SOL`}
                </Subtitle>
              </LeftStatDiv>
              {time && leaderboardInfo ? (
                time === LeaderboardTime.WEEK ? (
                  <RightStatDiv>
                    <StatTitle>
                      {primaryInfo.sold === primaryInfo.supply ? `7D VOLUME` : `AVAILABILITY`}
                    </StatTitle>
                    <Subtitle>
                      {primaryInfo.sold === primaryInfo.supply
                        ? `${
                            Math.round((leaderboardInfo.sevenDays / LAMPORTS_PER_SOL) * 100) / 100
                          } SOL`
                        : `${primaryInfo.supply - primaryInfo.sold} LEFT`}
                    </Subtitle>
                  </RightStatDiv>
                ) : time === LeaderboardTime.MONTH ? (
                  <RightStatDiv>
                    <StatTitle>
                      {primaryInfo.sold === primaryInfo.supply ? `30D VOLUME` : `AVAILABILITY`}
                    </StatTitle>
                    <Subtitle>
                      {primaryInfo.sold === primaryInfo.supply
                        ? `${
                            Math.round((leaderboardInfo.thirtyDays / LAMPORTS_PER_SOL) * 100) / 100
                          } SOL`
                        : `${primaryInfo.supply - primaryInfo.sold} LEFT`}
                    </Subtitle>
                  </RightStatDiv>
                ) : (
                  <RightStatDiv>
                    <StatTitle>
                      {primaryInfo.sold === primaryInfo.supply ? `TOTAL VOLUME` : `AVAILABILITY`}
                    </StatTitle>
                    <Subtitle>
                      {primaryInfo.sold === primaryInfo.supply
                        ? `${
                            Math.round((leaderboardInfo.allTime / LAMPORTS_PER_SOL) * 100) / 100
                          } SOL`
                        : `${primaryInfo.supply - primaryInfo.sold} LEFT`}
                    </Subtitle>
                  </RightStatDiv>
                )
              ) : (
                <RightStatDiv>
                  <StatTitle>
                    {primaryInfo.sold === primaryInfo.supply ? `TOTAL VOLUME` : `AVAILABILITY`}
                  </StatTitle>
                  <Subtitle>
                    {primaryInfo.sold === primaryInfo.supply
                      ? `${
                          Math.round(
                            ((secondaryInfo.volume + primaryInfo.sold * primaryInfo.price) /
                              LAMPORTS_PER_SOL) *
                              100
                          ) / 100
                        } SOL`
                      : `${primaryInfo.supply - primaryInfo.sold} LEFT`}
                  </Subtitle>
                </RightStatDiv>
              )}
            </Details>
          )}
        </HeaderDiv>
      </DetailsDiv>
    </ItemDiv>
  )
}

export default React.memo(BaseVideoCard)
